import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getStudentReportData } from '../practice-exams/ReportCards';
import type { ExternalResultSummary, LgsRank } from '../../types/external-school';

// Herkese açık karne sayfalarının ortak parçaları (giriş gerektirmez, panel kenar çubuğu yok).

export const KarneShell = ({ schoolName, children }: { schoolName?: string | null; children: React.ReactNode }) => {
    const { systemConfig } = useAuth();
    return (
        <div className="min-h-screen bg-slate-50">
            <header className="border-b border-slate-200 bg-white">
                <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3">
                    <img src={systemConfig?.companyLogo || '/logo.svg'} alt={systemConfig?.companyName || 'Logo'} className="h-9 w-9 rounded object-contain" />
                    <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-slate-900">{systemConfig?.companyName || 'YES Akademi'}</div>
                        {schoolName && <div className="truncate text-xs text-slate-500">{schoolName}</div>}
                    </div>
                </div>
            </header>
            <main className="mx-auto max-w-5xl px-4 py-6">{children}</main>
        </div>
    );
};

export const saveBlob = ({ blob, fileName }: { blob: Blob; fileName?: string }, fallback: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || fallback;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const formatNet = (value: number | null | undefined) =>
    typeof value === 'number' ? value.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-';

export const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleDateString('tr-TR') : '');

export const rankText = (rank?: LgsRank | null) => (rank?.rank ? `${rank.rank} / ${rank.participantCount}` : '-');

// Karne yanıtı (öğrencinin kendi karnesiyle aynı yapı) → web karne bileşeninin verisi
export const buildReportData = (response: any) => {
    const exam = response?.exam || {};
    const examMetadata = {
        bookletMapping: response?.bookletMapping || {},
        testNames: response?.testNames || {},
        testAverages: response?.testAverages || {},
        overallStats: response?.overallStats || {},
        levelType: exam.levelType,
        yksMode: exam.yksMode,
        systemType: exam.systemType || (exam.isInternal ? 'internal' : undefined),
    };
    return {
        ...getStudentReportData(response?.result || {}, examMetadata),
        examId: exam.id,
        additionalInfo: response?.additionalInfo || null,
    };
};

export const RankCards = ({ summary, schoolLabel = 'Okul sırası' }: { summary: Pick<ExternalResultSummary, 'totalNet' | 'ranks'>; schoolLabel?: string }) => (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Toplam net</div>
            <div className="mt-1 text-2xl font-bold text-slate-900">{formatNet(summary.totalNet)}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{schoolLabel}</div>
            <div className="mt-1 text-2xl font-bold text-slate-900">{rankText(summary.ranks?.school)}</div>
            {summary.ranks?.school && <div className="text-xs text-slate-500">ilk %{summary.ranks.school.topPercent.toLocaleString('tr-TR')}</div>}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Genel sıra</div>
            <div className="mt-1 text-2xl font-bold text-slate-900">{rankText(summary.ranks?.general)}</div>
            {summary.ranks?.general && <div className="text-xs text-slate-500">ilk %{summary.ranks.general.topPercent.toLocaleString('tr-TR')}</div>}
        </div>
    </div>
);

export const RankNote = () => (
    <p className="text-xs text-slate-500">
        Netlerde 3 yanlış 1 doğruyu götürür. Sıralamalar bu denemeye YES'e yüklenen öğrenciler içindir; yayınevinin Türkiye geneli sıralaması değildir.
    </p>
);
